import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  AdminPricingRulesResponse,
  CreatePricingRuleRequest,
  PricingRuleWithConflicts,
} from '@/api/types';

export type PricingFilters = {
  locationId?: string;
  sportCode?: string;
};

export function useAdminPricingRules(filters: PricingFilters = {}) {
  const params = new URLSearchParams();
  if (filters.locationId) params.set('locationId', filters.locationId);
  if (filters.sportCode) params.set('sportCode', filters.sportCode);
  const qs = params.toString();
  return useQuery({
    queryKey: ['adminPricingRules', filters],
    queryFn: () => apiClient<AdminPricingRulesResponse>(`/admin/pricing-rules${qs ? `?${qs}` : ''}`),
  });
}

export function useCreatePricingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreatePricingRuleRequest) =>
      apiClient<PricingRuleWithConflicts>('/admin/pricing-rules', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminPricingRules'] });
    },
  });
}

export function useUpdatePricingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...request }: CreatePricingRuleRequest & { id: string }) =>
      apiClient<PricingRuleWithConflicts>(`/admin/pricing-rules/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminPricingRules'] });
    },
  });
}

export function useDeletePricingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient<void>(`/admin/pricing-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminPricingRules'] });
    },
  });
}
